/**
 * 剪切/拷贝/粘贴（补充：DESIGN.md §6.1 的编辑菜单，走系统剪贴板）。
 *
 * 拷贝的是**合成后的可见内容**（compositeRegion），不是某一层的原始像素——
 * 系统剪贴板要跟其他应用互通，粘到别处去的应该是"看起来是什么"，不是
 * 图层内部构造。粘贴则反过来新建一个图层承接内容，跟自由变换/新建图层
 * 一样遵守"结果落在一个独立图层上，不悄悄改动别的图层"这条惯例。
 *
 * 剪切 = 拷贝 + 清空当前图层的对应区域，复用 `clearSelectedPixels`——
 * 没有选区时清空整个活动图层，同 Alt+Delete 的"没有选区就整层"的行为。
 */
import { layerId, type LayerId } from '../engine/tile/key';
import type { TileStore } from '../engine/tile/store';
import { applyFill } from '../engine/fill/apply';
import type { RasterLayer } from '../document/types';
import { layerDefaults } from '../document/draft';
import { createAddLayerCommand } from './commands/layer-commands';
import { compositeRegion } from './composite';
import { clearSelectedPixels } from './selection-edit';
import type { AppStore } from './store';
import type { ClipboardGateway, ClipboardImage } from '../io/clipboard';

/**
 * 拷贝：把选区（或整个文档）的合成结果写进系统剪贴板。
 *
 * `selection.contentBounds()` 是块粒度的（含选区的那几个 256×256 块的外接矩形，
 * 不是选区本身的精确像素范围——跟 fillSelectionWithColor/clearSelectedPixels
 * 同一个既有精度），所以合成完还要按 `selection.valueAt` 把 bounds 里
 * **没被选中**的那部分改成透明，否则不规则选区（或选区没占满所在块）
 * 会把周围没选中的内容也悄悄带进剪贴板。
 */
export async function copyToClipboard(store: AppStore, clipboard: ClipboardGateway): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  const rawSelection = store.selection;
  const selection = rawSelection !== null && !rawSelection.isEmpty ? rawSelection : null;
  const bounds = selection !== null ? selection.contentBounds() : { x: 0, y: 0, ...document.size };
  if (bounds === null || bounds.width === 0 || bounds.height === 0) return false;

  const pixels = new Uint8Array(bounds.width * bounds.height * 4);
  compositeRegion(document, store.tiles, bounds, pixels);
  if (selection !== null) {
    const row = new Uint8Array(bounds.width);
    for (let y = 0; y < bounds.height; y++) {
      selection.readRow(bounds.y + y, bounds.x, bounds.x + bounds.width, row);
      for (let x = 0; x < bounds.width; x++) {
        const at = (y * bounds.width + x) * 4 + 3;
        pixels[at] = Math.round((pixels[at]! * row[x]!) / 255);
      }
    }
  }
  await clipboard.writeImage({ width: bounds.width, height: bounds.height, pixels });
  return true;
}

/** 剪切：拷贝之后清空活动图层对应区域，一次调用两条历史（拷贝不进历史）。 */
export async function cutToClipboard(store: AppStore, clipboard: ClipboardGateway): Promise<boolean> {
  const copied = await copyToClipboard(store, clipboard);
  if (!copied) return false;
  clearSelectedPixels(store); // 没内容可清也不算失败——拷贝这一步已经成功了
  return true;
}

export interface PasteDeps {
  readonly tiles: TileStore;
  readonly nextLayerId: () => string;
  readonly setActiveLayer: (id: LayerId) => void;
}

/**
 * 粘贴：剪贴板里的图片落成一个新图层，居中摆放（裁到文档范围内）。
 * 新建图层 + 像素内容合成**一条**历史（`dispatchWithPixels`，同裁剪的做法），
 * undo 一步全部撤掉，不会撤出一个空图层。
 */
export async function pasteFromClipboard(
  store: AppStore,
  clipboard: ClipboardGateway,
  deps: PasteDeps,
  image?: ClipboardImage,
): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  const picture = image ?? (await clipboard.readImage());

  const id = layerId(deps.nextLayerId());
  const layer: RasterLayer = {
    ...layerDefaults(id, '粘贴的图层'),
    kind: 'raster',
    tiles: deps.tiles.handle(id),
    bounds: { x: 0, y: 0, width: 0, height: 0 },
  };

  const px = Math.max(0, Math.round((document.size.width - picture.width) / 2));
  const py = Math.max(0, Math.round((document.size.height - picture.height) / 2));
  const width = Math.min(picture.width, document.size.width - px);
  const height = Math.min(picture.height, document.size.height - py);

  if (width > 0 && height > 0) {
    const transaction = store.beginPixelTransaction('粘贴');
    const touched = applyFill(
      store.tiles,
      id,
      { x: px, y: py, width, height },
      (x, y) => {
        const at = ((y - py) * picture.width + (x - px)) * 4;
        return [picture.pixels[at]!, picture.pixels[at + 1]!, picture.pixels[at + 2]!, picture.pixels[at + 3]!];
      },
      transaction,
    );
    store.dispatchWithPixels(
      createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
      transaction.end(),
    );
    store.invalidatePixels(touched);
  } else {
    // 图片比文档还大，裁完什么都不剩——仍然新建一个空图层，而不是悄悄什么都不做
    store.dispatch(
      createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
    );
  }
  deps.setActiveLayer(id);
  return true;
}
